import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const BG = "#F8F4EE";
const CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const GREEN = "#5C8A5C";
const DOCUMENTS_KEY = "yoma_documents";

export default function ProjectSummaryPage({
  /** Si défini, limite aux projets dont client_email correspond à cet email. */
  clientEmail = null,
  /** OPC mandaté : pas de données financières (MAD), uniquement avancement, jalons, alertes. */
  isOPCMandate = false,
  cabinetId = null,
}) {
  const [projects, setProjects] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const cabinetName =
    typeof window !== "undefined"
      ? localStorage.getItem("yoma_company_name") || "MON CABINET"
      : "MON CABINET";

  const restrictKey =
    clientEmail == null || String(clientEmail).trim() === ""
      ? "__ALL__"
      : `ce:${String(clientEmail).trim().toLowerCase()}`;

  useEffect(() => {
    if (cabinetId == null) return;
    loadAll();
  }, [restrictKey, cabinetId]);

  async function loadAll() {
    // 1. Charger les projets (projects a cabinet_id)
    const projectsRes = await supabase.from("projects").select("*").eq("cabinet_id", cabinetId);

    let projectList = projectsRes.data || [];
    if (clientEmail != null && String(clientEmail).trim() !== "") {
      const em = String(clientEmail).trim().toLowerCase();
      projectList = projectList.filter((p) => (p.client_email || "").trim().toLowerCase() === em);
    }
    setProjects(projectList);
    if (clientEmail != null && String(clientEmail).trim() !== "") {
      if (projectList.length === 1) {
        setSelectedProjectId(String(projectList[0].id));
      } else if (projectList.length === 0) {
        setSelectedProjectId("");
      }
    }

    // 2. Obtenir les IDs des projets pour filtrer finance/payment_schedule
    const ids = (projectsRes.data || []).map((p) => p.id);

    // 3. milestones a cabinet_id ; finance et payment_schedule n'ont PAS cabinet_id
    const [milestonesRes, financeRes, scheduleRes] = await Promise.all([
      supabase.from("milestones").select("*").eq("cabinet_id", cabinetId),
      ids.length > 0
        ? supabase.from("finance").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
      ids.length > 0
        ? supabase.from("payment_schedule").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
    ]);

    setFinanceEntries(financeRes.data || []);
    setMilestones(milestonesRes.data || []);
    setSchedule(scheduleRes.data || []);
  }

  /** Chaque montant MAD : {isOPCMandate ? "—" : `${valeur} MAD`} (valeur formatée fr-FR) */
  function formatMAD(raw) {
    const n = Number(raw ?? 0);
    const valeur = Number.isFinite(n) ? n.toLocaleString("fr-FR") : String(raw ?? "");
    return isOPCMandate ? "—" : `${valeur} MAD`;
  }

  function getExpectedAmount(projectBudget, percentage) {
    const budget = Number(projectBudget || 0);
    const pct = Number(percentage || 0);
    if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (budget * pct) / 100;
  }

  function getProjectFinance(projectId) {
    return financeEntries.filter(
      (entry) => String(entry.project_id) === String(projectId)
    );
  }

  function getProjectMilestones(projectId) {
    return milestones.filter(
      (m) => String(m.project_id) === String(projectId)
    );
  }

  function getProjectSchedule(projectId) {
    return schedule.filter(
      (row) => String(row.project_id) === String(projectId)
    );
  }

  function getEncaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "income")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getDecaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "expense")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getSoldeProjet(projectId) {
    return getEncaisseProjet(projectId) - getDecaisseProjet(projectId);
  }

  function getProgressProjet(projectId) {
    const list = getProjectMilestones(projectId);
    if (list.length === 0) return 0;
    const done = list.filter((m) => m.status === "done").length;
    return Math.round((done / list.length) * 100);
  }

  function getEncaisseInstallment(projectId, installmentNumber) {
    return financeEntries
      .filter(
        (entry) =>
          String(entry.project_id) === String(projectId) &&
          entry.type === "income" &&
          Number(entry.installment_number) === Number(installmentNumber)
      )
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getScheduleStats(project) {
    const rows = getProjectSchedule(project.id);

    let conformes = 0;
    let partiels = 0;
    let impayes = 0;

    rows.forEach((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const encaisse = getEncaisseInstallment(project.id, row.installment_number);

      if (encaisse >= attendu && attendu > 0) {
        conformes += 1;
      } else if (encaisse > 0 && encaisse < attendu) {
        partiels += 1;
      } else {
        impayes += 1;
      }
    });

    return { conformes, partiels, impayes };
  }

  function getTotalTheorique(project) {
    return getProjectSchedule(project.id).reduce((sum, row) => {
      return sum + getExpectedAmount(project.budget, row.percentage);
    }, 0);
  }

  function getResteAEncaisser(project) {
    const theorique = getTotalTheorique(project);
    const encaisse = getEncaisseProjet(project.id);
    return Math.max(theorique - encaisse, 0);
  }

  function getProfitActuel(projectId) {
    return getEncaisseProjet(projectId) - getDecaisseProjet(projectId);
  }

  function getProfitFinalPrevisionnel(project) {
    return getTotalTheorique(project) - getDecaisseProjet(project.id);
  }

  function getMargeFinalePrevisionnelle(project) {
    const theorique = getTotalTheorique(project);
    const profitFinal = getProfitFinalPrevisionnel(project);

    if (theorique <= 0) return 0;
    return (profitFinal / theorique) * 100;
  }

  function getRisqueTresorerie(project) {
    const solde = getSoldeProjet(project.id);
    const reste = getResteAEncaisser(project);
    const budget = Number(project.budget || 0);

    if (solde < 0 || reste > budget * 0.5) return "Critique";
    if (solde < budget * 0.1 || reste > budget * 0.25) return "Tension";
    return "Sain";
  }

  function handlePrint() {
    window.print();
  }

  /** Jalons échéances : statut conforme / partiel / impayé (aligné AlertsPage) */
  function getScheduleTimeline(project) {
    const rows = getProjectSchedule(project.id).slice().sort((a, b) => Number(a.installment_number) - Number(b.installment_number));
    return rows.map((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const enc = getEncaisseInstallment(project.id, row.installment_number);
      let statut = "impayé";
      let color = "#B85C3A";
      if (enc >= attendu && attendu > 0) {
        statut = "conforme";
        color = GREEN;
      } else if (enc > 0 && enc < attendu) {
        statut = "partiel";
        color = "#D68910";
      }
      return {
        id: row.id ?? `${project.id}-v-${row.installment_number}`,
        label: row.label || `V${row.installment_number}`,
        v: row.installment_number,
        attendu,
        enc,
        statut,
        color,
      };
    });
  }

  /** Alertes actives pour le projet (même logique métier que AlertsPage, périmètre projet) */
  function getActiveAlertsForProject(project) {
    const out = [];
    const projectMilestones = getProjectMilestones(project.id);
    const projectSchedule = getProjectSchedule(project.id);

    const solde = getSoldeProjet(project.id);
    if (solde < 0) {
      out.push({
        id: `${project.id}-solde-negatif`,
        niveau: "critique",
        titre: "Solde négatif",
        detail: `Solde : ${formatMAD(solde)}`,
      });
    }

    let impayesCount = 0;
    projectSchedule.forEach((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const enc = getEncaisseInstallment(project.id, row.installment_number);
      if (enc === 0 && attendu > 0) {
        impayesCount += 1;
        out.push({
          id: `${project.id}-impaye-${row.installment_number}`,
          niveau: "critique",
          titre: `Échéance impayée — V${row.installment_number}`,
          detail: `${row.label || ""} · Attendu ${formatMAD(attendu)}`,
        });
      } else if (enc > 0 && enc < attendu) {
        out.push({
          id: `${project.id}-partiel-${row.installment_number}`,
          niveau: "attention",
          titre: `Échéance partielle — V${row.installment_number}`,
          detail: `Reçu ${formatMAD(enc)} / ${formatMAD(attendu)}`,
        });
      }
      const relatedMilestone = projectMilestones.find(
        (m) => Number(m.number) === Number(row.milestone_number)
      );
      if (relatedMilestone && relatedMilestone.status === "done" && enc === 0) {
        out.push({
          id: `${project.id}-jalon-sans-paiement-${row.installment_number}`,
          niveau: "attention",
          titre: "Jalon validé sans paiement",
          detail: `Jalon ${row.milestone_number} · V${row.installment_number}`,
        });
      }
    });

    const totalMilestones = projectMilestones.length;
    const doneMilestones = projectMilestones.filter((m) => m.status === "done").length;
    const progress = totalMilestones > 0 ? Math.round((doneMilestones / totalMilestones) * 100) : 0;
    if (progress < 20 && impayesCount >= 2) {
      out.push({
        id: `${project.id}-projet-bloque`,
        niveau: "critique",
        titre: "Projet bloqué",
        detail: `Avancement ${progress}% · ${impayesCount} échéances impayées`,
      });
    }

    return out;
  }

  function getDaysRemaining(project) {
    const end =
      project.end_date ||
      project.date_fin ||
      project.delivery_date ||
      project.deadline ||
      null;
    if (!end) return null;
    const endDate = new Date(end);
    if (Number.isNaN(endDate.getTime())) return null;
    const now = new Date();
    const diff = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return diff;
  }

  const selectedProject = projects.find(
    (p) => String(p.id) === String(selectedProjectId)
  );

  const scheduleStats = selectedProject
    ? getScheduleStats(selectedProject)
    : { conformes: 0, partiels: 0, impayes: 0 };

  const scheduleTimeline = selectedProject ? getScheduleTimeline(selectedProject) : [];
  const activeAlerts = selectedProject ? getActiveAlertsForProject(selectedProject) : [];

  const recentDocuments = useMemo(() => {
    if (!selectedProjectId) return [];
    try {
      const raw = localStorage.getItem(DOCUMENTS_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      const forProject = arr.filter(
        (d) => String(d.projectId) === String(selectedProjectId)
      );
      let list = forProject.slice(-3).reverse();
      if (isOPCMandate) {
        list = list.filter(
          (d) => d.typeDocument !== "Devis" && d.typeDocument !== "Facture"
        );
      }
      return list;
    } catch {
      return [];
    }
  }, [selectedProjectId, isOPCMandate]);

  const statutBadge = (status) => {
    const s = (status || "").toLowerCase();
    let bg = "#EEF4FD";
    let color = "#2E6DB4";
    if (s.includes("chantier") || s.includes("construction")) {
      bg = "#E8F6EF";
      color = GREEN;
    }
    if (s.includes("livré") || s.includes("term")) {
      bg = "#F3EEE7";
      color = GOLD;
    }
    return { bg, color, label: status || "—" };
  };

  const selectStyle = {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #E7E1D8",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontSize: 14,
    color: TAUPE,
    background: CARD,
    minWidth: 260,
    maxWidth: "100%",
    cursor: "pointer",
    outline: "none",
  };

  const daysLeft = selectedProject ? getDaysRemaining(selectedProject) : null;

  return (
    <main
      style={{
        minHeight: "100vh",
        background: BG,
        padding: "28px 24px 48px",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: TAUPE,
        boxSizing: "border-box",
      }}
    ><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
          @media print {
            body { background: white !important; }
            button, select { display: none !important; }
            main { padding: 0 !important; }
          }
          @media (max-width: 960px) {
            .ps-grid { grid-template-columns: 1fr !important; }
            .ps-kpi { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
          }
          @media (max-width: 520px) {
            .ps-kpi { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 720px) {
            .ps-docs { grid-template-columns: 1fr !important; }
          }
        `}
      </style><header
        style={{
          maxWidth: 1100,
          marginLeft: "auto",
          marginRight: "auto",
          marginBottom: 24,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
        }}
      ><div style={{ flex: "1 1 280px" }}><div style={{ fontSize: 12, color: "#8A7B6D", marginBottom: 6, letterSpacing: "0.04em" }}>
            {cabinetName}
          </div><h1
            style={{
              margin: 0,
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 32,
              fontWeight: 600,
              color: TAUPE,
              lineHeight: 1.2,
            }}
          >
            Synthèse Projet
          </h1><p style={{ margin: "8px 0 0", fontSize: 15, color: "#6F6256" }}>
            Vue globale de pilotage projet YOMA
          </p></div><div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}><select
            style={selectStyle}
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          ><option value="">Sélectionner un projet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {selectedProject && (
            <button
              type="button"
              onClick={handlePrint}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: `1px solid ${GOLD}`,
                background: CARD,
                color: GOLD,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Imprimer / Export PDF
            </button>
          )}
        </div></header>

      {!selectedProject && (
        <div
          style={{
            maxWidth: 1100,
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
            padding: "48px 24px",
          }}
        ><p
            style={{
              margin: "0 0 8px",
              fontFamily: "'Playfair Display', serif",
              fontSize: 22,
              fontWeight: 600,
              color: TAUPE,
            }}
          >
            Sélectionnez un projet
          </p><p style={{ margin: "0 0 28px", fontSize: 14, color: "#8B7361", maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
            Choisissez un projet dans la liste ci-dessus pour afficher la synthèse, les indicateurs et le suivi des jalons.
          </p>
          {projects.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 12,
                textAlign: "left",
                marginTop: 8,
              }}
            >
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedProjectId(String(p.id))}
                  style={{
                    background: CARD,
                    border: "1px solid #E7E1D8",
                    borderRadius: 12,
                    padding: "14px 16px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "'DM Sans', sans-serif",
                    transition: "box-shadow 0.15s",
                  }}
                ><div style={{ fontWeight: 700, fontSize: 14, color: TAUPE, marginBottom: 4 }}>{p.name}</div><div style={{ fontSize: 12, color: "#8B7361" }}>{p.client_name || "Client —"}</div></button>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 14, color: "#9A9088" }}>Aucun projet disponible pour le moment.</p>
          )}
        </div>
      )}

      {selectedProject && (
        <><div
            className="ps-kpi"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 14,
              maxWidth: 1100,
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: 24,
            }}
          >{!isOPCMandate && (
            <div
              style={{
                background: CARD,
                borderRadius: 12,
                padding: "16px 18px",
                border: "1px solid #E7E1D8",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Budget total (MAD)
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: GOLD }}>
                {formatMAD(selectedProject.budget)}
              </div></div>
          )}{!isOPCMandate && (
            <div
              style={{
                background: CARD,
                borderRadius: 12,
                padding: "16px 18px",
                border: "1px solid #E7E1D8",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Encaissé (MAD)
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: GREEN }}>
                {formatMAD(getEncaisseProjet(selectedProject.id))}
              </div></div>
          )}<div
              style={{
                background: CARD,
                borderRadius: 12,
                padding: "16px 18px",
                border: "1px solid #E7E1D8",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Avancement jalons (%)
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: GOLD, marginBottom: 8 }}>
                {getProgressProjet(selectedProject.id)}%
              </div><div style={{ height: 8, background: "#F0EBE3", borderRadius: 999, overflow: "hidden" }}><div
                  style={{
                    height: "100%",
                    width: `${getProgressProjet(selectedProject.id)}%`,
                    background: `linear-gradient(90deg, ${GOLD}, #E8C87A)`,
                    borderRadius: 999,
                    transition: "width 0.35s",
                  }}
                /></div></div><div
              style={{
                background: CARD,
                borderRadius: 12,
                padding: "16px 18px",
                border: "1px solid #E7E1D8",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Jours restants
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: TAUPE }}>
                {daysLeft === null ? "—" : `${daysLeft} j.`}
              </div></div></div><div
            className="ps-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              maxWidth: 1100,
              marginLeft: "auto",
              marginRight: "auto",
              alignItems: "start",
            }}
          ><div style={{ display: "flex", flexDirection: "column", gap: 20 }}><section
                style={{
                  background: CARD,
                  borderRadius: 14,
                  padding: "20px 22px",
                  border: "1px solid #E7E1D8",
                }}
              ><h2 style={{ margin: "0 0 14px", fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                  Informations projet
                </h2><div style={{ display: "grid", gap: 10, fontSize: 14 }}><div><span style={{ color: "#8B7361", fontSize: 12 }}>Client</span><div style={{ fontWeight: 600 }}>{selectedProject.client_name || "—"}</div></div><div><span style={{ color: "#8B7361", fontSize: 12 }}>Type / statut</span><div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}><span style={{ textTransform: "capitalize" }}>{selectedProject.status || "—"}</span>
                      {(() => {
                        const b = statutBadge(selectedProject.status);
                        return (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: b.bg,
                              color: b.color,
                            }}
                          >
                            {b.label}
                          </span>
                        );
                      })()}
                    </div></div><div><span style={{ color: "#8B7361", fontSize: 12 }}>Localisation</span><div>{selectedProject.location || selectedProject.zone || "—"}</div></div><div><span style={{ color: "#8B7361", fontSize: 12 }}>Description / périmètre</span><div style={{ fontSize: 13, lineHeight: 1.45 }}>{selectedProject.description || "—"}</div></div>
                {!isOPCMandate && (
                  <div><span style={{ color: "#8B7361", fontSize: 12 }}>Budget</span><div style={{ fontWeight: 600, color: GOLD }}>{formatMAD(selectedProject.budget)}</div></div>
                )}
              </div></section>
              {!isOPCMandate && (
                <section
                  style={{
                    background: CARD,
                    borderRadius: 14,
                    padding: "20px 22px",
                    border: "1px solid #E7E1D8",
                  }}
                ><h2 style={{ margin: "0 0 14px", fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                    État financier
                  </h2><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}><div><div style={{ fontSize: 11, color: "#8B7361", marginBottom: 4 }}>Encaissé</div><div style={{ fontWeight: 700, color: GREEN }}>{formatMAD(getEncaisseProjet(selectedProject.id))}</div></div><div><div style={{ fontSize: 11, color: "#8B7361", marginBottom: 4 }}>Décaissé</div><div style={{ fontWeight: 700, color: "#B85C3A" }}>{formatMAD(getDecaisseProjet(selectedProject.id))}</div></div><div><div style={{ fontSize: 11, color: "#8B7361", marginBottom: 4 }}>Solde</div><div style={{ fontWeight: 700, color: TAUPE }}>{formatMAD(getSoldeProjet(selectedProject.id))}</div></div></div><div style={{ fontSize: 12, color: "#6F6256", marginBottom: 8 }}>
                    Théorique échéances : {formatMAD(getTotalTheorique(selectedProject))} · Reste à encaisser :{" "}
                    {formatMAD(getResteAEncaisser(selectedProject))}
                  </div><div style={{ height: 10, background: "#F0EBE3", borderRadius: 999, overflow: "hidden" }}><div
                      style={{
                        height: "100%",
                        width: `${Math.min(100, getTotalTheorique(selectedProject) > 0 ? (getEncaisseProjet(selectedProject.id) / getTotalTheorique(selectedProject)) * 100 : 0)}%`,
                        background: GREEN,
                        borderRadius: 999,
                      }}
                    /></div><div style={{ marginTop: 10, fontSize: 12, color: "#8B7361" }}>
                    Risque trésorerie : <strong style={{ color: TAUPE }}>{getRisqueTresorerie(selectedProject)}</strong> · Marge prévisionnelle :{" "}
                    {getMargeFinalePrevisionnelle(selectedProject).toFixed(1)}%
                  </div><div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F0EBE3", display: "grid", gap: 6, fontSize: 13 }}><div><span style={{ color: "#8B7361" }}>Total théorique (échéances)</span>{" "}
                    <strong>{formatMAD(getTotalTheorique(selectedProject))}</strong></div><div><span style={{ color: "#8B7361" }}>Profit actuel</span> <strong>{formatMAD(getProfitActuel(selectedProject.id))}</strong></div><div><span style={{ color: "#8B7361" }}>Profit final prévisionnel</span>{" "}
                    <strong>{formatMAD(getProfitFinalPrevisionnel(selectedProject))}</strong></div></div></section>
              )}
              </div><div style={{ display: "flex", flexDirection: "column", gap: 20 }}><section
                style={{
                  background: CARD,
                  borderRadius: 14,
                  padding: "20px 22px",
                  border: "1px solid #E7E1D8",
                }}
              ><h2 style={{ margin: "0 0 14px", fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                  Jalons (échéances)
                </h2><p style={{ margin: "0 0 12px", fontSize: 12, color: "#8B7361" }}>
                  Conforme · Partiel · Impayé — {scheduleStats.conformes} / {scheduleStats.partiels} / {scheduleStats.impayes}
                </p><div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {scheduleTimeline.length === 0 ? (
                    <p style={{ fontSize: 13, color: "#9A9088" }}>Aucune échéance planifiée.</p>
                  ) : (
                    scheduleTimeline.map((item, idx) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          padding: "10px 0",
                          borderBottom: idx < scheduleTimeline.length - 1 ? "1px solid #F0EBE3" : "none",
                        }}
                      ><div
                          style={{
                            width: 10,
                            minHeight: 10,
                            borderRadius: "50%",
                            background: item.color,
                            marginTop: 5,
                            flexShrink: 0,
                          }}
                        /><div style={{ flex: 1 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}><div><div style={{ fontWeight: 700, fontSize: 14 }}>
                                V{item.v} — {item.label}
                              </div>{!isOPCMandate && (
                                <div style={{ fontSize: 12, color: "#8B7361", marginTop: 2 }}>
                                  {formatMAD(item.enc)} / {formatMAD(item.attendu)}
                                </div>
                              )}</div><span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                padding: "3px 8px",
                                borderRadius: 6,
                                background: item.statut === "conforme" ? "#E8F6EF" : item.statut === "partiel" ? "#FEF9E7" : "#FDEDEC",
                                color: item.statut === "conforme" ? GREEN : item.statut === "partiel" ? "#D68910" : "#B85C3A",
                              }}
                            >
                              {item.statut === "conforme" ? "Conforme" : item.statut === "partiel" ? "Partiel" : "Impayé"}
                            </span></div></div></div>
                    ))
                  )}
                </div></section><section
                style={{
                  background: CARD,
                  borderRadius: 14,
                  padding: "20px 22px",
                  border: "1px solid #E7E1D8",
                }}
              ><h2 style={{ margin: "0 0 14px", fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                  Alertes actives
                </h2>
                {activeAlerts.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13, color: "#9A9088" }}>Aucune alerte active pour ce projet.</p>
                ) : (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                    {activeAlerts.map((a) => (
                      <li
                        key={a.id}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "1px solid #E7E1D8",
                          borderLeft: `3px solid ${a.niveau === "critique" ? "#B85C3A" : GOLD}`,
                          background: "#FDFBF8",
                        }}
                      ><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}><span style={{ fontWeight: 700, fontSize: 13 }}>{a.titre}</span>
                          {a.niveau === "critique" && (
                            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#fff", background: "#B85C3A", padding: "2px 8px", borderRadius: 999 }}>
                              CRITIQUE
                            </span>
                          )}
                        </div><div style={{ fontSize: 12, color: "#6F6256" }}>{a.detail}</div></li>
                    ))}
                  </ul>
                )}
              </section></div></div><section
            style={{
              maxWidth: 1100,
              marginLeft: "auto",
              marginRight: "auto",
              marginTop: 28,
            }}
          ><h2 style={{ margin: "0 0 14px", fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
              Documents récents
            </h2>
            {recentDocuments.length === 0 ? (
              <p style={{ fontSize: 13, color: "#9A9088", margin: 0 }}>Aucun document local récent pour ce projet (stockage navigateur).</p>
            ) : (
              <div className="ps-docs" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
                {recentDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      background: CARD,
                      border: "1px solid #E7E1D8",
                      borderRadius: 12,
                      padding: "12px 14px",
                    }}
                  ><span
                      style={{
                        display: "inline-block",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: GOLD,
                        marginBottom: 6,
                      }}
                    >
                      {doc.typeDocument || "Document"}
                    </span><div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, lineHeight: 1.35 }}>{doc.titre || "Sans titre"}</div><div style={{ fontSize: 11, color: "#9A9088" }}>{doc.dateCreation || "—"}</div></div>
                ))}
              </div>
            )}
          </section></>
      )}
    </main>
  );
}
